import type { ChatResponse } from "../api/types";
import { store } from "../state";

type StageState = "idle" | "active" | "skipped";

interface Stage {
  id: string;
  label: string;
  /** Resumo curto do que a etapa produziu neste turno. */
  detail: string;
  state: StageState;
}

/**
 * Pipeline Cognitivo: mostra por onde o turno passou, e nao apenas o que ele respondeu.
 *
 * Cada etapa e lida do que o backend REALMENTE reportou (InteractionDiagnostics e os campos de
 * uso da resposta). Uma etapa que nao produziu nada aparece como "nao ocorreu", nunca apagada e
 * nunca como falha — a diferenca entre "o modelo nao extraiu relacao" e "a extracao quebrou" e
 * exatamente o que uma ferramenta de explicabilidade existe para preservar.
 *
 * Nesta versao o pipeline reflete o turno CONCLUIDO. Acender etapa por etapa durante o
 * processamento depende de o backend emitir progresso parcial, que hoje ele nao emite.
 */
export class PipelineStrip {
  constructor(private readonly root: HTMLElement) {
    store.subscribe((state) => this.render(state.lastResponse));
    this.render(null);
  }

  private render(response: ChatResponse | null): void {
    // Antes do primeiro turno nao ha o que explicar. No celular a faixa ociosa e escondida por
    // CSS: seis etapas vazias custariam uma linha inteira da area de leitura para dizer nada.
    this.root.classList.toggle("idle", response === null);
    const stages = this.buildStages(response);
    this.root.innerHTML = `
      <ol class="pipeline">
        ${stages
          .map(
            (stage, index) => `
              <li class="pipeline-stage ${stage.state}">
                <span class="pipeline-dot" aria-hidden="true"></span>
                <span class="pipeline-label">${stage.label}</span>
                <span class="pipeline-detail">${stage.detail}</span>
              </li>
              ${index < stages.length - 1 ? '<li class="pipeline-arrow" aria-hidden="true">→</li>' : ""}
            `
          )
          .join("")}
      </ol>
    `;
  }

  private buildStages(response: ChatResponse | null): Stage[] {
    if (!response) {
      return [
        stage("message", "Mensagem", "aguardando", "idle"),
        stage("entities", "Entidades", "—", "idle"),
        stage("facts", "Fatos", "—", "idle"),
        stage("relations", "Relações", "—", "idle"),
        stage("retrieval", "Recuperação", "—", "idle"),
        stage("answer", "Resposta", "—", "idle")
      ];
    }

    const diagnostics = response.interactionDiagnostics;
    const entities = diagnostics?.entities.length ?? 0;
    const facts = diagnostics?.facts.length ?? 0;
    const relations = diagnostics?.relations.length ?? 0;
    const sources =
      (response.memorySourceIds?.length ?? 0) + (response.knowledgeSourceIds?.length ?? 0);
    const retrievalUsed = response.memoryUsed || response.knowledgeUsed || response.cognitiveGraphUsed;

    return [
      stage("message", "Mensagem", diagnostics?.interactionType ?? "recebida", "active"),
      stage("entities", "Entidades", count(entities), entities > 0 ? "active" : "skipped"),
      stage("facts", "Fatos", count(facts), facts > 0 ? "active" : "skipped"),
      stage("relations", "Relações", count(relations), relations > 0 ? "active" : "skipped"),
      stage(
        "retrieval",
        "Recuperação",
        retrievalUsed ? `${sources} fonte${sources === 1 ? "" : "s"}` : "não usada",
        retrievalUsed ? "active" : "skipped"
      ),
      stage("answer", "Resposta", `${(response.elapsedMs / 1000).toFixed(1)}s`, "active")
    ];
  }
}

function stage(id: string, label: string, detail: string, state: StageState): Stage {
  return { id, label, detail, state };
}

function count(value: number): string {
  return value === 0 ? "nenhuma" : String(value);
}
